/// <amd-module name="Release/DeploymentsSection/DeploymentsTab" />
import * as React from "react";

import {
    IVssComponentContext,
    VssComponent
} from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssContributionService } from "VSS/Platform/Contribution";
import * as RMContracts from "Release/Client/Contracts/Release";
import { Build } from 'Build/Client/Contracts/Build';
import { DeploymentsComponentStore, IDeploymentsState, Deployments } from "Release/DeploymentsSection/Stores/DeploymentsComponentStore";
import { DeploymentsComponentActionsCreator } from "Release/DeploymentsSection/Actions/DeploymentsComponentActionsCreator";
import { DeploymentsComponentActions } from "Release/DeploymentsSection/Actions/DeploymentsComponentActions";
import { DeploymentsSignalRHub } from "Release/DeploymentsSection/SignalRHub";
import { SignalRStore } from "Release/DeploymentsSection/Stores/SignalR";
import { DeploymentsSignalRActionCreator, DeploymentsSignalRActionHub } from "Release/DeploymentsSection/Actions/SignalR";
import { DeploymentsSource } from "DeploymentsSource";
import { EnvironmentsChain, IActiveReleaseEnvironmentsCellProps } from "Release/EnvironmentNode/EnvironmentChain";

import { Card } from "VSSUI/Card";
import { Table, TableColumnLayout, ITableColumn, SimpleTableCell } from "VSSUI/Table";
import * as Resources from "Release/DeploymentsSection/Resources";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { ScreenBreakpoints } from "VSS/Core/Util/Screen";

interface IDeploymentsTabLaunchState {
    deploymentsCount: number;
}

interface IDeploymentsTabLauncherProps {
    build: Build;
}

export class DeploymentsTabLauncher extends VssComponent<IDeploymentsTabLauncherProps, IDeploymentsTabLaunchState> {
    private _actionsCreator: DeploymentsComponentActionsCreator;
    private _store: DeploymentsComponentStore;
    private _signalRHub: DeploymentsSignalRHub;
    private _signalRStore: SignalRStore;
    private _actions: DeploymentsComponentActions;
    private _deploymentsSignalRActionHub: DeploymentsSignalRActionHub;
    private _deploymentsSignalRActionCreator: DeploymentsSignalRActionCreator;

    constructor(props: IDeploymentsTabLauncherProps, context: IVssComponentContext) {
        super(props, context);
        this._actions = new DeploymentsComponentActions();
        this._initializeFlux();
        this._initializeSignalRHub(this.props.build);
    }

    public render() {
        let deploymentComponentProps = {
            store: this._store,
            actionsCreator: this._actionsCreator,
            signalRHub: this._signalRHub
        } as IDeploymentsTabProps;

        return <DeploymentsTab {...deploymentComponentProps} />;
    }

    public componentDidMount() {
        this._store.addChangedListener(this._onStoreChanged);
        this._actions.setDeploymentsData.addListener(this._onChange);
        this.trackPromise(this._actionsCreator.initializeData(this.props.build));
    }

    public componentWillUnmount(): void {
        this._actions.setDeploymentsData.removeListener(this._onChange);
        this._store.removeChangedListener(this._onStoreChanged);
        this._store.dispose();
    }

    private _onChange = () => {
        this.forceUpdate();
    }

    private _onStoreChanged = (): void => {
        let state = this._store.getState();
        let totalDeploymentsCount = state.succeededDeploymentsCount + state.inProgressDeploymentsCount + state.failedDeploymentsCount + state.partiallySucceededCount;
        this.setState({
            deploymentsCount: totalDeploymentsCount
        });
    }

    private _initializeFlux(): void {
        this._deploymentsSignalRActionHub = new DeploymentsSignalRActionHub();
        this._deploymentsSignalRActionCreator = new DeploymentsSignalRActionCreator({
            actionHub: this._deploymentsSignalRActionHub
        });
        this._signalRStore = new SignalRStore({
            actionCreator: this._deploymentsSignalRActionCreator
        });

        this._store = new DeploymentsComponentStore(this._actions, this._signalRStore);
        let source = new DeploymentsSource(this.context.pageContext);
        this._actionsCreator = new DeploymentsComponentActionsCreator(this._actions, source);
        this.state = {
            deploymentsCount: 0
        };
    }

    private _initializeSignalRHub(build: any) {
        const vssPageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        let contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        let data = contributionService.getData<any>("ms.vss-releaseManagement-web.rm-signalr-data-provider-new");

        const vssPageData = vssPageService.getData();
        if (vssPageData && data && data.connectionUrl) {
            const hostId = vssPageData.hostId;
            const isHosted = vssPageData.isHosted;

            this._signalRHub = new DeploymentsSignalRHub({
                actionCreator: this._deploymentsSignalRActionCreator,
                hostInformation: {
                    hostId: hostId,
                    isHosted: isHosted,
                    hostPath: data.connectionUrl.split("_apis")[0],
                    projectId: build.project.id
                },
                projectId: build.project.id
            });
        }
    }
}

interface IDeploymentsTabProps {
    actionsCreator: DeploymentsComponentActionsCreator;
    store: DeploymentsComponentStore;
    signalRHub: DeploymentsSignalRHub;
}

interface IDeploymentsTabState extends IDeploymentsState {
}

const columns = [
    {
        columnLayout: TableColumnLayout.singleLinePrefix,
        id: "name",
        name: Resources.ColumnName,
        readonly: true,
        renderCell: renderNameCell,
        width: -40
    },
    {
        id: "stages",
        name: "",
        readonly: true,
        renderCell: renderStageCell,
        width: -60
    }
] as ITableColumn<IDeploymentTableItem>[];

export interface IDeploymentTableItem extends Deployments {
}

function renderNameCell<T extends IDeploymentTableItem>(
    rowIndex: number,
    columnIndex: number,
    tableColumn: ITableColumn<T>,
    tableItem: T
): JSX.Element {
    return (
        <SimpleTableCell
            className={"bolt-table-cell-primary"}
            columnIndex={columnIndex}
            contentClassName={"text-ellipsis body-m"}
            key={columnIndex}
            tableColumn={tableColumn}
        >
            <FPSLink href={tableItem.rdUrl} className="defintion-name" telemetrySource="deployments-summary-view-definition-link">
                {tableItem.rdName}
            </FPSLink>
            <span>/</span>
            <FPSLink href={tableItem.releaseUrl} className="release-name" supportsFPS={false} telemetrySource="deployments-summary-view-release-link">
                {tableItem.releaseName}
            </FPSLink>
        </SimpleTableCell>
    );
}

function renderStageCell<T extends IDeploymentTableItem>(
    rowIndex: number,
    columnIndex: number,
    tableColumn: ITableColumn<T>,
    tableItem: T
): JSX.Element {
    return (
        <SimpleTableCell
            className={"bolt-table-cell-primary"}
            columnIndex={columnIndex}
            contentClassName={"text-ellipsis body-m"}
            key={columnIndex}
            tableColumn={tableColumn}
        >
            {renderActiveReleaseEnvironmentsChain(tableItem)}
        </SimpleTableCell>
    );
}

function renderActiveReleaseEnvironmentsChain(environments: Deployments): JSX.Element {
    const createMap = (environments: RMContracts.ReleaseEnvironment[]): { [key: number]: number } => {
        let map: { [key: number]: number } = {};
        if (!environments) {
            return map;
        }

        environments.map((environment: RMContracts.ReleaseEnvironment) => {
            map[environment.definitionEnvironmentId] = environment.releaseId;
        });

        return map;
    }

    let environmentChainProps: IActiveReleaseEnvironmentsCellProps = {
        cssClass: "deployments-rel-env-chain flex-row",
        environments: environments.environments,
        releaseEnvironmentNodeCss: "deployments-rel-env-node",
        definitionEnvironmentCurrentReleaseMap: createMap(environments.environments),
        releaseId: environments.id,
        definitionId: environments.rdId,
        envNodeWidth: 96,
        projectId: environments.projectId,
        onExpandClick: (widthOnExpand: number) => { }
    };

    return <EnvironmentsChain {...environmentChainProps} />;
}

class DeploymentsTab extends VssComponent<IDeploymentsTabProps, IDeploymentsTabState> {
    constructor(props: IDeploymentsTabProps, context: IVssComponentContext) {
        super(props, context);
        this.state = props.store.getState();
        this._items = new ArrayItemProvider<IDeploymentTableItem>(this.state.deployments.map(this._getTableItem));
    }

    public render() {
        return <Card className="flex-grow bolt-table-card" contentProps={{ contentPadding: false }}>
            <Table
                columns={columns}
                ariaLabel={Resources.AriaReleasesTable}
                itemProvider={this._items}
                role="table"
                tableBreakpoints={
                    [
                        { breakpoint: ScreenBreakpoints.xsmall, columnWidths: [-100, 0] },
                        { breakpoint: ScreenBreakpoints.small, columnWidths: [-100, 0] },
                    ]
                } />
        </Card>;
    }

    public componentDidMount(): void {
        this.props.store.addChangedListener(this._onStoreChanged);
        this._attachSignalRListener();
    }

    public componentWillUnmount(): void {
        this.props.store.removeChangedListener(this._onStoreChanged);
        this.props.store.dispose();
    }

    private _onStoreChanged = (): void => {
        this.setState(this.props.store.getState());
        // go dumb and just replace
        this._items = new ArrayItemProvider<IDeploymentTableItem>(this.state.deployments.map(this._getTableItem));
        this._attachSignalRListener();
    };

    private _attachSignalRListener() {
        this.state.deployments.forEach(deployment => {
            let activeEnvironments = deployment.environments.filter(environment => {
                if (
                    environment.status != RMContracts.EnvironmentStatus.Canceled &&
                    environment.status != RMContracts.EnvironmentStatus.PartiallySucceeded &&
                    environment.status != RMContracts.EnvironmentStatus.Rejected &&
                    environment.status != RMContracts.EnvironmentStatus.Succeeded
                ) {
                    return environment;
                }
            });

            const signalRHub = this.props.signalRHub;
            if (signalRHub) {
                activeEnvironments.length > 0
                    ? signalRHub.watchRelease(deployment.projectId, deployment.id)
                    : signalRHub.stopWatchingRelease(deployment.projectId, deployment.id);
            }
        });
    }

    private _getTableItem = (d: Deployments): IDeploymentTableItem => {
        return d;
    }

    private _items: ArrayItemProvider<IDeploymentTableItem>;
}